/** Read-only view of one submitted form using the original form definition. */
import { useEffect, useRef, useState } from 'react';
import { getEditableForm, getSubmittedFormData } from '../../api/forms-api';
import { useUiLocale } from '../../app/ui-locale';
import { ErrorMessage, Loading } from '../../components/Loading';
import type { DynamicFormDefinition, FormValues } from '../../models/form';
import { printDynamicFormsElement } from '../../utils/print-document';
import { FormRenderer } from './FormRenderer';
import { publicCopy } from './public-copy';

export function SubmissionDetailApp({ formId, submittedFormId }: { formId: number; submittedFormId: number }) {
  const { locale, formatNumber } = useUiLocale();
  const copy = publicCopy(locale);
  const [form, setForm] = useState<DynamicFormDefinition>();
  const [values, setValues] = useState<FormValues>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const printRef = useRef<HTMLElement>(null);

  useEffect(() => {
    Promise.all([getEditableForm(formId), getSubmittedFormData(submittedFormId)])
      .then(([definition, data]) => {
        setForm(definition);
        setValues(data);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [formId, submittedFormId]);

  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;
  if (!form) return <div className="alert alert-warning">{copy.submissionNotFound}</div>;

  return <section ref={printRef} className="df-submission-detail df-printable-report df-print-source">
    <header className="df-page-header no-print">
      <div>
        <h2>{form.Title}</h2>
        <p>{copy.submissionNumber(formatNumber(submittedFormId))}</p>
      </div>
      <div className="df-print-actions">
        <button type="button" className="btn btn-success" onClick={() => printDynamicFormsElement(printRef.current, { title: `${form.Title} - ${submittedFormId}` })}>{copy.savePdf}</button>
        <button type="button" className="btn btn-default" onClick={() => printDynamicFormsElement(printRef.current, { title: `${form.Title} - ${submittedFormId}` })}>{copy.print}</button>
      </div>
    </header>
    <div className="alert alert-info df-print-note no-print">
      {copy.pdfHelp}
    </div>
    <div className="df-print-only-header print-only"><h1>{form.Title}</h1><p>{copy.submissionNumber(formatNumber(submittedFormId))}</p></div>
    <FormRenderer form={form} initialValues={values} readOnly showAllReadOnlyTabs omitVerificationFields secureSubmissionId={submittedFormId} submissionFileAccess="viewer" />
  </section>;
}
